import { useState } from 'react';
import { GroupService } from '@/api/services/groupService';
import {
  CreateGroupParams,
  UpdateGroupParams,
  AddGroupParticipantsParams,
  RemoveGroupParticipantsParams,
  GroupInfo,
  GroupMember,
  SearchParams
} from '@/api/types';
import AblyManager from '@/utils/ably-im/ably-manager';
import { useFriendStore } from '@/stores/useFriendStore';
import { logPrint } from '@/utils/my-utils';
import { useGroupStore } from '@/stores/useGroupStore';

/**
 * 群组操作相关的 hook
 * 处理创建、更新、解散群组等一次性操作
 */
export function useGroupActions() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  /**
   * 执行群组操作的通用方法
   * @param operation 操作函数
   * @param params 操作参数
   * @param successMessage 成功提示信息
   * @returns Promise<boolean> 操作是否成功
   */
  const executeOperation = async <T>(
    operation: (...args: any[]) => Promise<void>,
    params: T | [string, T] | [string],
  ): Promise<boolean> => {
    setIsLoading(true);
    setError('');

    try {
      if (Array.isArray(params)) {
        await operation(...params);
      } else {
        await operation(params);
      }
      return true;
    } catch (err: any) {
      console.error('群组操作失败:', err);
      const errorMessage = err?.err_msg || '操作失败，请重试';
      setError(errorMessage);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * 创建群组
   * @param params 创建群组参数
   * @returns Promise<GroupInfo> 创建的群组信息
   */
  const createGroup = async (params: CreateGroupParams): Promise<GroupInfo> => {
    // 接口不需要 handles 参数，所以这里需要手动删除
    const result = await GroupService.createGroup({
      name: params.name,
      invitee_ids: params.invitee_ids,
    });

    // 订阅群组频道
    AblyManager.getInstance().subscribeToGroupChannel(result.id);

    // 通知所有成员（包括邀请的用户）监听群组
    const memberHandles = params.handles || [];
    logPrint('向群组成员发送群组创建通知:', memberHandles);

    AblyManager.getInstance().notifyUsersToListenGroup(
      'group_created',
      result.id,
      memberHandles
    );

    return result;
  };

  /**
   * 更新群组信息
   * @param groupId 群组ID
   * @param params 更新参数
   * @returns Promise<boolean> 是否更新成功
   */
  const updateGroup = async (groupId: string, params: UpdateGroupParams): Promise<boolean> => {
    return executeOperation(
      GroupService.updateGroup,
      [groupId, params],
    );
  };

  /**
   * 离开群组
   * @param groupId 群组ID
   * @returns Promise<boolean> 是否离开成功
   */
  const leaveGroup = async (groupId: string): Promise<boolean> => {
    return executeOperation(
      GroupService.leaveGroup,
      [groupId],
    );
  };

  /**
   * 解散群组
   * @param groupId 群组ID
   * @returns Promise<boolean> 是否解散成功
   */
  const disbandGroup = async (groupId: string): Promise<boolean> => {
    return executeOperation(
      GroupService.disbandGroup,
      [groupId],
    );
  };

  /**
   * 添加群组成员
   * @param groupId 群组ID
   * @param params 添加成员参数
   * @returns Promise<boolean> 是否添加成功
   */
  const addParticipants = async (groupId: string, params: AddGroupParticipantsParams): Promise<boolean> => {
    setIsLoading(true);
    setError('');

    try {
      // 先获取要添加的用户的handle信息
      const friendMap = useFriendStore.getState().friendMap;

      // 执行添加操作
      await GroupService.addParticipants(groupId, params);

      // 收集新添加成员的handle
      const newMemberHandles: number[] = [];
      params.participant_ids.forEach(userId => {
        const friend = friendMap.get(userId);
        if (friend?.handle) {
          newMemberHandles.push(friend.handle);
        }
      });

      // 通知新成员监听群组
      if (newMemberHandles.length > 0) {
        logPrint('向新添加的群组成员发送入群通知:', newMemberHandles);

        AblyManager.getInstance().notifyUsersToListenGroup(
          'group_enter',
          groupId,
          newMemberHandles
        );
      }

      return true;
    } catch (err: any) {
      console.error('群组操作失败:', err);
      const errorMessage = err?.err_msg || '操作失败，请重试';
      setError(errorMessage);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * 移除群组成员
   * @param groupId 群组ID
   * @param params 移除成员参数
   * @returns Promise<boolean> 是否移除成功
   */
  const removeParticipants = async (groupId: string, params: RemoveGroupParticipantsParams): Promise<boolean> => {
    setIsLoading(true);
    setError('');

    try {
      // 先获取要移除的用户的handle信息
      const friendMap = useFriendStore.getState().friendMap;

      // 收集被移除成员的handle
      const removedMemberHandles: number[] = [];
      params.participant_ids.forEach(userId => {
        const friend = friendMap.get(userId);
        if (friend?.handle) {
          removedMemberHandles.push(friend.handle);
        }
      });

      // 执行移除操作
      await GroupService.removeParticipants(groupId, params);

      // 通知被移除的成员停止监听群组
      if (removedMemberHandles.length > 0) {
        logPrint('向被移除的群组成员发送移除通知:', removedMemberHandles);

        AblyManager.getInstance().notifyUsersToListenGroup(
          'group_rmove',
          groupId,
          removedMemberHandles
        );
      }

      return true;
    } catch (err: any) {
      console.error('群组操作失败:', err);
      const errorMessage = err?.err_msg || '操作失败，请重试';
      setError(errorMessage);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * 获取群组信息（带状态管理）
   * @param groupId 群组ID
   * @returns Promise<GroupInfo | null> 群组信息
   */
  const getGroupInfo = async (groupId: string): Promise<GroupInfo | null> => {
    setIsLoading(true);
    setError('');

    try {
      const groupInfo = await GroupService.getGroupInfo(groupId);
      logPrint('群组信息获取成功');
      if (groupInfo) {
        useGroupStore.getState().setGroupInfo(groupId, groupInfo)
      }
      return groupInfo;
    } catch (err: any) {
      console.error('获取群组信息失败:', err);
      const errorMessage = err?.err_msg || '获取群组信息失败';
      setError(errorMessage);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * 获取群组成员列表
   * @param groupId 群组ID
   * @param params 搜索参数
   * @returns Promise<GroupMember[]> 群组成员列表
   */
  const getGroupMembers = async (groupId: string, params?: SearchParams): Promise<GroupMember[]> => {
    try {
      setIsLoading(true);
      const members = await GroupService.getParticipantList(groupId, params);
      
      // 更新 store 中的群组成员信息
      const { setGroupInfo, getGroupInfoById } = useGroupStore.getState();
      const existingGroupInfo = getGroupInfoById(groupId);
      if (existingGroupInfo) {
        setGroupInfo(groupId, {
          ...existingGroupInfo,
          participants: members
        });
      }
      return members;
    } catch (error) {
      console.error('获取群组成员失败:', error);
      setError('获取群组成员失败');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * 设置群组公告
   * @param groupId 群组ID
   * @param content 公告内容
   * @returns Promise<boolean> 是否设置成功
   */
  const setGroupAnnouncement = async (groupId: string, content: string): Promise<boolean> => {
    return executeOperation(
      GroupService.setGroupAnnouncement,
      [groupId, { content }]
    );
  };

  /**
   * 删除群组公告
   * @param groupId 群组ID
   * @returns Promise<boolean> 是否删除成功
   */
  const removeGroupAnnouncement = async (groupId: string): Promise<boolean> => {
    return executeOperation(
      GroupService.removeGroupAnnouncement,
      [groupId]
    );
  };

  /**
   * 清除错误信息
   */
  const clearError = () => {
    setError('');
  };

  return {
    // 状态
    isLoading,
    error,

    // 操作方法
    createGroup,
    updateGroup,
    leaveGroup,
    disbandGroup,
    addParticipants,
    removeParticipants,
    getGroupInfo,
    getGroupMembers,
    setGroupAnnouncement,
    removeGroupAnnouncement,

    // 工具方法
    clearError
  };
}